import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import payrollService from '../../../services/payrollService';
import NotificationToast from '../../../components/ui/NotificationToast';

const DetailedPayslipModal = ({ isOpen, onClose, payrollRecordId, employeeName }) => {
  const [payrollData, setPayrollData] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    if (isOpen && payrollRecordId) {
      loadPayslipData();
    }
  }, [isOpen, payrollRecordId]);

  const loadPayslipData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [payrollResult, earningsResult, paymentsResult] = await Promise.all([
        payrollService.getPayrollRecord(payrollRecordId),
        payrollService.getEarningsBreakdown(payrollRecordId),
        payrollService.getPaymentsBreakdown(payrollRecordId)
      ]);

      if (payrollResult.success) {
        setPayrollData(payrollResult.data);
      }

      if (earningsResult.success) {
        setEarnings(earningsResult.data || []);
      }

      if (paymentsResult.success) {
        setPayments(paymentsResult.data || []);
      }

      if (!payrollResult.success) {
        setError(payrollResult.error || 'Failed to load payslip data');
      }
    } catch (err) {
      setError('Failed to load payslip data');
      console.error('Error loading payslip:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayslipHTML = async () => {
    console.log('[DetailedPayslipModal] fetchPayslipHTML called');
    console.log('[DetailedPayslipModal] payrollRecordId:', payrollRecordId);

    // Get auth token from localStorage
    const token = localStorage.getItem('erp_auth_token') || localStorage.getItem('auth_token');
    console.log('[DetailedPayslipModal] Token exists:', !!token);
    if (!token) {
      console.error('[DetailedPayslipModal] No auth token found');
      setNotification({ message: 'Authentication required. Please log in again.', type: 'error' });
      return null;
    }

    // Get API base URL from environment or use default
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ||
      import.meta.env.VITE_ERP_API_URL ||
      (window.location.hostname === 'localhost' ? `http://${window.location.hostname}:8000/api` : window.location.origin + '/api');
    console.log('[DetailedPayslipModal] API_BASE_URL:', API_BASE_URL);

    // Fetch payslip HTML using authenticated request
    const payslipUrl = `${API_BASE_URL}/payroll/${payrollRecordId}/payslip?format=html`;
    console.log('[DetailedPayslipModal] Payslip URL:', payslipUrl);
    console.log('[DetailedPayslipModal] Request headers:', {
      'Authorization': `Bearer ${token.substring(0, 20)}...`,
      'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
    });

    try {
      const response = await fetch(payslipUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8'
        }
      });

      console.log('[DetailedPayslipModal] Response status:', response.status);
      console.log('[DetailedPayslipModal] Response statusText:', response.statusText);
      console.log('[DetailedPayslipModal] Response headers:', {
        'Content-Type': response.headers.get('Content-Type'),
        'Content-Length': response.headers.get('Content-Length')
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[DetailedPayslipModal] Payslip fetch error:', errorText);
        console.error('[DetailedPayslipModal] Error response status:', response.status);
        throw new Error(`Failed to fetch payslip: ${response.status} ${response.statusText}`);
      }

      const contentType = response.headers.get('Content-Type');
      console.log('[DetailedPayslipModal] Response Content-Type:', contentType);

      const htmlContent = await response.text();
      console.log('[DetailedPayslipModal] Response content length:', htmlContent.length);
      console.log('[DetailedPayslipModal] Response content preview (first 200 chars):', htmlContent.substring(0, 200));
      console.log('[DetailedPayslipModal] Is HTML?', htmlContent.trim().startsWith('<!DOCTYPE') || htmlContent.trim().startsWith('<html'));
      console.log('[DetailedPayslipModal] Is JSON?', htmlContent.trim().startsWith('{') || htmlContent.trim().startsWith('['));

      if (htmlContent.trim().startsWith('{') || htmlContent.trim().startsWith('[')) {
        console.error('[DetailedPayslipModal] ERROR: Received JSON instead of HTML!');
        console.error('[DetailedPayslipModal] Full response:', htmlContent);
        throw new Error('Server returned JSON instead of HTML. Please check backend configuration.');
      }

      return htmlContent;
    } catch (error) {
      console.error('[DetailedPayslipModal] Exception in fetchPayslipHTML:', error);
      throw error;
    }
  };

  const handleExport = async () => {
    console.log('[DetailedPayslipModal] handleExport called');
    if (!payrollRecordId) {
      console.error('[DetailedPayslipModal] No payrollRecordId provided');
      return;
    }

    try {
      console.log('[DetailedPayslipModal] Fetching payslip HTML...');
      const htmlContent = await fetchPayslipHTML();
      if (!htmlContent) {
        console.error('[DetailedPayslipModal] No HTML content received');
        return;
      }

      console.log('[DetailedPayslipModal] Opening new window with HTML content');
      // Open new window and write HTML content
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        console.log('[DetailedPayslipModal] Writing HTML to new window');
        newWindow.document.write(htmlContent);
        newWindow.document.close();
        console.log('[DetailedPayslipModal] Payslip opened successfully');
        setNotification({ message: 'Payslip opened in new window', type: 'success' });
      } else {
        console.error('[DetailedPayslipModal] Failed to open new window (popup blocked?)');
        setNotification({ message: 'Please allow popups to view the payslip', type: 'error' });
      }
    } catch (error) {
      console.error('[DetailedPayslipModal] Error exporting payslip:', error);
      console.error('[DetailedPayslipModal] Error stack:', error.stack);
      setNotification({ message: 'Failed to export payslip: ' + (error.message || 'Unknown error'), type: 'error' });
    }
  };

  const handlePrint = async () => {
    console.log('[DetailedPayslipModal] handlePrint called');
    if (!payrollRecordId) {
      console.error('[DetailedPayslipModal] No payrollRecordId provided');
      return;
    }

    try {
      console.log('[DetailedPayslipModal] Fetching payslip HTML for printing...');
      const htmlContent = await fetchPayslipHTML();
      if (!htmlContent) {
        console.error('[DetailedPayslipModal] No HTML content received');
        return;
      }

      console.log('[DetailedPayslipModal] Opening print window with HTML content');
      // Open new window and write HTML content
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        console.log('[DetailedPayslipModal] Writing HTML to print window');
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        console.log('[DetailedPayslipModal] HTML written, waiting for load...');

        // Wait for content to load, then trigger print
        printWindow.onload = () => {
          console.log('[DetailedPayslipModal] Print window loaded, triggering print...');
          setTimeout(() => {
            printWindow.print();
            console.log('[DetailedPayslipModal] Print dialog triggered');
          }, 250);
        };

        // Fallback if onload doesn't fire
        setTimeout(() => {
          if (printWindow && !printWindow.closed) {
            console.log('[DetailedPayslipModal] Fallback: triggering print...');
            printWindow.print();
          }
        }, 500);

        setNotification({ message: 'Print dialog opened', type: 'success' });
      } else {
        console.error('[DetailedPayslipModal] Failed to open print window (popup blocked?)');
        setNotification({ message: 'Please allow popups to print the payslip', type: 'error' });
      }
    } catch (error) {
      console.error('[DetailedPayslipModal] Error printing payslip:', error);
      console.error('[DetailedPayslipModal] Error stack:', error.stack);
      setNotification({ message: 'Failed to print payslip: ' + (error.message || 'Unknown error'), type: 'error' });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (!isOpen) return null;

  const totalEarnings = earnings.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
  const totalPaid = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-background rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Detailed Payslip</h2>
              <p className="text-sm text-muted-foreground mt-1">{employeeName}</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleExport} iconName="Download" iconPosition="left">
                Export Payslip
              </Button>
              <Button variant="outline" onClick={handlePrint} iconName="Printer" iconPosition="left">
                Print Payslip
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <Icon name="X" size={20} />
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <div className="flex gap-1 px-6">
            {[
              { id: 'summary', label: 'Summary', icon: 'FileText' },
              { id: 'earnings', label: 'Earnings Breakdown', icon: 'TrendingUp' },
              { id: 'payments', label: 'Payment History', icon: 'CreditCard' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 font-medium text-sm border-b-2 transition-colors ${activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
              >
                <div className="flex items-center gap-2">
                  <Icon name={tab.icon} size={16} />
                  {tab.label}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <NotificationToast
            message={notification?.message}
            type={notification?.type}
            onDismiss={() => setNotification(null)}
          />

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading payslip data...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-destructive">{error}</p>
              <Button variant="outline" onClick={loadPayslipData} className="mt-4">
                Retry
              </Button>
            </div>
          ) : !payrollData ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No payslip data available</p>
            </div>
          ) : (
            <>
              {/* Summary Tab */}
              {activeTab === 'summary' && (
                <div className="space-y-6">
                  {/* Pay Period Info */}
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-semibold text-foreground mb-4">Pay Period Information</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Period Start</p>
                        <p className="text-sm font-medium text-foreground">
                          {formatDate(payrollData.pay_period_start)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Period End</p>
                        <p className="text-sm font-medium text-foreground">
                          {formatDate(payrollData.pay_period_end)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Status</p>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${payrollData.status === 'paid' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                          payrollData.status === 'processed' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                            'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                          }`}>
                          {payrollData.status}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Processed Date</p>
                        <p className="text-sm font-medium text-foreground">
                          {payrollData.processed_date ? formatDate(payrollData.processed_date) : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Earnings Summary */}
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-semibold text-foreground mb-4">Earnings</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-muted-foreground">Basic Salary</span>
                        <span className="text-sm font-medium text-foreground">
                          {formatCurrency(payrollData.basic_salary)}
                        </span>
                      </div>
                      {parseFloat(payrollData.hra || 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">HRA</span>
                          <span className="text-sm font-medium text-foreground">
                            {formatCurrency(payrollData.hra)}
                          </span>
                        </div>
                      )}
                      {parseFloat(payrollData.allowances || 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Allowances</span>
                          <span className="text-sm font-medium text-foreground">
                            {formatCurrency(payrollData.allowances)}
                          </span>
                        </div>
                      )}
                      {parseFloat(payrollData.overtime_pay || 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Overtime Pay</span>
                          <span className="text-sm font-medium text-foreground">
                            {formatCurrency(payrollData.overtime_pay)}
                          </span>
                        </div>
                      )}
                      {parseFloat(payrollData.bonus || 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Bonus</span>
                          <span className="text-sm font-medium text-foreground">
                            {formatCurrency(payrollData.bonus)}
                          </span>
                        </div>
                      )}
                      {earnings.length > 0 && (
                        <div className="flex justify-between pt-2 border-t border-border">
                          <span className="text-sm text-muted-foreground">Additional Earnings (from breakdown)</span>
                          <span className="text-sm font-medium text-foreground">
                            {formatCurrency(totalEarnings)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-border font-semibold">
                        <span className="text-foreground">Gross Salary</span>
                        <span className="text-foreground">
                          {formatCurrency(payrollData.gross_salary)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Deductions Summary */}
                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-semibold text-foreground mb-4">Deductions</h3>
                    <div className="space-y-2">
                      {parseFloat(payrollData.employee_pf || 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Provident Fund (PF)</span>
                          <span className="text-sm font-medium text-foreground">
                            {formatCurrency(payrollData.employee_pf)}
                          </span>
                        </div>
                      )}
                      {parseFloat(payrollData.professional_tax || 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Professional Tax</span>
                          <span className="text-sm font-medium text-foreground">
                            {formatCurrency(payrollData.professional_tax)}
                          </span>
                        </div>
                      )}
                      {parseFloat(payrollData.tax_deduction || 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Tax Deduction (TDS)</span>
                          <span className="text-sm font-medium text-foreground">
                            {formatCurrency(payrollData.tax_deduction)}
                          </span>
                        </div>
                      )}
                      {parseFloat(payrollData.medical_insurance || 0) > 0 && (
                        <div className="flex justify-between">
                          <span className="text-sm text-muted-foreground">Medical Insurance</span>
                          <span className="text-sm font-medium text-foreground">
                            {formatCurrency(payrollData.medical_insurance)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-border font-semibold">
                        <span className="text-foreground">Total Deductions</span>
                        <span className="text-foreground">
                          {formatCurrency(payrollData.deductions)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Net Pay */}
                  <div className="bg-success/5 border border-success/20 rounded-lg p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-foreground">Net Pay</span>
                      <span className="text-2xl font-bold text-success">
                        {formatCurrency(payrollData.net_salary)}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Earnings Breakdown Tab */}
              {activeTab === 'earnings' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">Earnings Breakdown</h3>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Total Additional Earnings</p>
                      <p className="text-xl font-bold text-foreground">{formatCurrency(totalEarnings)}</p>
                    </div>
                  </div>

                  {earnings.length === 0 ? (
                    <div className="text-center py-12 bg-card border border-border rounded-lg">
                      <Icon name="FileText" size={48} className="mx-auto mb-4 opacity-50 text-muted-foreground" />
                      <p className="text-muted-foreground">No earnings breakdown available</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {earnings.map((earning) => (
                        <div
                          key={earning.id}
                          className="bg-card border border-border rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${earning.earning_type === 'overtime' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300' :
                                  earning.earning_type === 'bonus' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                                    earning.earning_type === 'allowance' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                                      earning.earning_type === 'incentive' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' :
                                        'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                                  }`}>
                                  {earning.earning_type.charAt(0).toUpperCase() + earning.earning_type.slice(1)}
                                </span>
                                <span className="text-sm text-muted-foreground">
                                  {formatDate(earning.earning_date)}
                                </span>
                              </div>
                              <p className="text-lg font-semibold text-foreground">
                                {formatCurrency(earning.amount)}
                              </p>
                              {earning.notes && (
                                <p className="text-sm text-muted-foreground mt-1">{earning.notes}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Payments Breakdown Tab */}
              {activeTab === 'payments' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-foreground">Payment History</h3>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Total Paid</p>
                      <p className="text-xl font-bold text-success">{formatCurrency(totalPaid)}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Remaining: {formatCurrency((payrollData.net_salary || 0) - totalPaid)}
                      </p>
                    </div>
                  </div>

                  {payments.length === 0 ? (
                    <div className="text-center py-12 bg-card border border-border rounded-lg">
                      <Icon name="CreditCard" size={48} className="mx-auto mb-4 opacity-50 text-muted-foreground" />
                      <p className="text-muted-foreground">No payment history available</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {payments.map((payment) => (
                        <div
                          key={payment.id}
                          className="bg-card border border-border rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${payment.payment_type === 'salary' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                                  payment.payment_type === 'advance' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                                    payment.payment_type === 'bonus' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                                      payment.payment_type === 'reimbursement' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300' :
                                        'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                                  }`}>
                                  {payment.payment_type.charAt(0).toUpperCase() + payment.payment_type.slice(1)}
                                </span>
                                <span className="text-sm text-muted-foreground">
                                  {formatDate(payment.payment_date)}
                                </span>
                              </div>
                              <p className="text-lg font-semibold text-foreground">
                                {formatCurrency(payment.amount)}
                              </p>
                              {payment.notes && (
                                <p className="text-sm text-muted-foreground mt-1">{payment.notes}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DetailedPayslipModal;

